<template>
  <div class="image-selector-container">
    <!-- Social Media Disclaimer -->
    <social-media-disclaimer
      v-if="showDisclaimer"
      @accepted="handleDisclaimerAccepted"
      @rejected="handleDisclaimerRejected"
    />

    <!-- Recommended Photo Angle Modal -->
    <div v-if="showRecommendedAngle" class="recommended-angle-modal" @click="handleModalBackdropClick">
      <div class="modal-content" @click.stop>
        <recommended-photo-angle
          :recommended_angle="recommended_angle"
          :not_recommended_angle="not_recommended_angle"
          :recommended_instruction="recommended_instruction"
          @proceed="handleRecommendedAngleProceed"
          @skip="handleRecommendedAngleSkip"
        />
      </div>
    </div>

    <!-- Multiple Pets Name Selection Modal -->
    <div v-if="showPetNameSelection" class="pet-name-selection-modal" @click="handlePetNameModalBackdropClick">
      <div class="modal-content" @click.stop>
        <pet-name-selector
          :petImage="selectedPetImage"
          :availableNames="availablePetNames"
          :currentIndex="currentImageIndex"
          :totalImages="selectedImages ? selectedImages.length : 0"
          :isPendingUpload="!!pendingUpload"
          :isNamingExisting="!pendingUpload && selectedImages.length > 0"
          @nameSelected="handlePetNameSelected"
          @back="handlePetNameBack"
        />
      </div>
    </div>

    <!-- Multiple Pets Input Page -->
    <div v-if="showMultiplePetsInput" class="multiple-pets-page">
      <multiple-pets-input
        :initialPets="multiplePetsList"
        @back="handleMultiplePetsBack"
        @proceed="handleMultiplePetsProceed"
        @petsUpdated="handlePetsUpdated"
      />
    </div>

    <!-- Main Content -->
    <div v-else-if="!showPetNameSelection">
      <!-- 大图预览区域 -->
      <div class="content-top">
        {{ name ? name.toUpperCase() : '' }}
      </div>
      <div class="content-center">
        <div class="content-left">
          <div class="image-container">
            <div
              class="cloudimage-360"
              id="gurkha-suv"
              :data-folder="name ? '/assets/360_degree/'+name+'/' : ''"
              data-filename-x="{index}.jpg"
              :data-amount-x="showed_image_amount"
              data-transformation="w=444&h=429&func=fit"
            ></div>
          </div>
        </div>
        <div class="content-right">
          <!-- Recommended Photo Angle Trigger -->
          <div class="recommended-angle-trigger-container">
            <button 
              class="recommended-angle-hint-btn" 
              @click="showRecommendedAngle = true"
              title="Recommended Photo Angles"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A5 5 0 0 0 8 8c0 1.3.5 2.6 1.5 3.5.8.8 1.3 1.5 1.5 2.5"></path>
                <path d="M9 18h6"></path>
                <path d="M10 22h4"></path>
              </svg>
              <span>Photo Tips</span>
            </button>
          </div>

          <div v-if="selectedOption === 'uploaded' && !showAdditionalCustomization" class="main-preview-area">
            <div class="selected-preview">
              <template v-if="selectedImages.length > 0">
                <img 
                  v-for="(img, index) in selectedImages" 
                  :key="'preview-'+index"
                  :src="img.url" 
                  class="main-preview-image"
                  @click="showFullscreen(img.url)"
                />
              </template>
              <div v-else class="empty-prompt">
                <span>To preview selected images, <br>upload and select at least one image</span>
              </div>
            </div>
          </div>

          <!-- Social Media Selection Area -->
          <div v-if="selectedOption === 'social' && !showAdditionalCustomization" class="social-media-area">
            <div class="social-media-content">
              <p class="social-media-title">Share your social media handle so our team can customize the product to fit your page</p>
              
              <div class="form-group">
                <div class="dropdown-wrapper">
                  <select v-model="selectedPlatform" class="platform-select">
                    <option value="" disabled>Select social media platform</option>
                    <option value="instagram">Instagram</option>
                    <option value="facebook">Facebook</option>
                    <option value="twitter">Twitter</option>
                    <option value="tiktok">TikTok</option>
                    <option value="youtube">YouTube</option>
                  </select>
                  <svg class="dropdown-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M7 10L12 15L17 10" stroke="#333" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
              </div>

              <div class="form-group">
                <div class="input-wrapper">
                  <span class="input-icon">✏️</span>
                  <input 
                    type="text" 
                    v-model="socialUsername" 
                    placeholder="Enter username"
                    class="username-input"
                  />
                  <!-- Previous Accounts Button -->
                  <button 
                    v-if="previousSocialAccounts.length > 0"
                    type="button"
                    class="history-button"
                    :class="{ 'active': showPreviousAccounts }"
                    @click="togglePreviousAccounts"
                    title="Select from previous accounts"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                  </button>
                </div>

                <!-- Previous Accounts Dropdown -->
                <div v-if="showPreviousAccounts" class="previous-accounts-dropdown">
                  <div class="dropdown-header">
                    <span>Previous accounts</span>
                    <button class="close-dropdown" @click="showPreviousAccounts = false">&times;</button>
                  </div>
                  <div class="dropdown-list">
                    <div 
                      v-for="(acc, index) in previousSocialAccounts" 
                      :key="index"
                      class="previous-account-item"
                      @click="selectPreviousAccount(acc)"
                    >
                      <div class="acc-info">
                        <span class="acc-platform">{{ acc.platform }}</span>
                        <span class="acc-username">@{{ acc.username }}</span>
                      </div>
                      <span class="select-hint">Select</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Independent Multiple Pets & Animal Portrait Style Section -->
          <div v-if="!showAdditionalCustomization" class="independent-style-section">
            <div class="style-link">
              <a href="#" @click.prevent="handlePortraitStyle">Multiple Pets & Animal Portrait Style</a>
            </div>
          </div>

          <!-- Additional Customization Panel -->
          <div v-if="showAdditionalCustomization" class="additional-customization-area">
            <div class="additional-customization-content">
              <h3 class="additional-title">Additional Customization</h3>
              
              <div class="customization-option">
                <div class="option-header">
                  <span class="option-title">Multiple Pets</span>
                  <label class="toggle-switch">
                    <input type="checkbox" v-model="multiplePetsEnabled">
                    <span class="toggle-slider"></span>
                  </label>
                </div>
                <p class="option-description">
                  Feature all your furry (or feathery) friends in one custom portrait. Perfect for multi-pet households or gifts that celebrate the whole crew.
                </p>
              </div>

              <div class="customization-option">
                <div class="option-header">
                  <span class="option-title">Animal Portrait Style</span>
                  <label class="toggle-switch">
                    <input type="checkbox" v-model="portraitStyleEnabled">
                    <span class="toggle-slider"></span>
                  </label>
                </div>
                <a href="#" class="view-outfit-link" @click.prevent="openOutfitSelector">View selected outfit</a>
                <p class="option-description">
                  Style your pet in an outfit of your choice—modern or medieval. Each portrait is illustrated to match your selected theme and your pet's unique personality.
                </p>
              </div>
              

              <p class="pricing-note">Each selection is $5. You can update your choice at anytime</p>

              <div class="back-button-container">
                <button class="back-button" @click="handleBackFromCustomization">Back</button>
              </div>
            </div>
          </div>

          <!-- 缩略图选择区域 -->
          <div class="image-selector">
            <div v-if="selectedOption === 'uploaded' && !showAdditionalCustomization" class="thumbnail-selector">
              <!-- <h3 class="section-title">{{ title }}</h3> -->
              
              <div class="thumbnail-grid">
                <!-- 合并后的图片列表 -->
                <div 
                  v-for="(image, index) in combinedList" 
                  :key="index"
                  class="thumbnail-item"
                  :class="{ 'selected': isSelected(index) }"
                  @click="toggleSelection(index)"
                >
                  <img 
                    :src="image.thumbnail || image.url" 
                    class="thumbnail-image"
                    @click.stop="showFullscreen(image.url)"
                  />
                  <!-- 选择指示器 -->
                  <div class="selection-indicator">
                    <div class="base-circle" :class="{ 'selected': isSelected(index) }"></div>
                    <div class="checkmark" v-if="isSelected(index)">✓</div>
                  </div>
                  <!-- 宠物名字标签 -->
                  <div v-if="multiplePetsEnabled && image.petName" class="pet-name-tag">
                    {{ image.petName }}
                  </div>
                  <div v-else-if="multiplePetsEnabled" class="no-name-tag">
                    No name
                  </div>
                </div>

                <!-- 上传按钮 -->
                <div 
                  v-if="uploadedImages.length < maxUpload" 
                  class="thumbnail-item upload-card" 
                  @click="triggerUpload"
                >
                  <input 
                    type="file" 
                    ref="fileInput" 
                    hidden 
                    multiple 
                    accept="image/*"
                    @change="handleFileUpload"
                  >
                  <div class="upload-content">
                    <span class="plus-icon">+</span>
                    <span class="upload-text">
                      {{ multiplePetsEnabled ? 'upload & name pet' : 'upload image' }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="!showAdditionalCustomization" class="toggle-switch-container">
              <div class="toggle-options" :class="{ 'social-active': selectedOption === 'social' }">
                <div class="toggle-option" 
                    :class="{ active: selectedOption === 'uploaded' }"
                    @click="selectedOption = 'uploaded'">
                  Upload Image
                </div>

                <div class="toggle-option"
                    :class="{ active: selectedOption === 'social' }"
                    @click="selectedOption = 'social'">
                  Social Media
                </div>
                <div class="or-divider-container">
                  <div class="or-divider-background"></div>
                  <div class="or-divider">OR</div>
                </div>
                <div class="slider-track"></div>
                <div class="slider-thumb" :class="selectedOption"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="button-container">
        <button class="nav-arrow left" @click="handleCancel">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        
        <div class="customization-text">
          {{ selectedOption === 'uploaded' ? 'Customization via uploaded images' : 'Customization via social media' }}
        </div>
        
        <button class="nav-arrow right" @click="next">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- 全屏预览 -->
    <div v-if="fullscreenImage" class="fullscreen-preview" @click="fullscreenImage = null">
      <img :src="fullscreenImage" class="fullscreen-image" />
    </div>

    <!-- Outfit Selector Page -->
    <div v-if="showOutfitSelector" class="outfit-selector-page">
      <!-- Outfit Confirmation View -->
      <div v-if="selectedOutfit && !showOutfitSwiper" class="outfit-confirmation">
        <div class="confirmation-header">
          <h1 class="confirmation-title">Animal Portrait Style</h1>
        </div>
        
        <div class="confirmation-content">
          <div class="selected-style-name">{{ selectedOutfitStyle }}</div>
          
          <div class="selected-outfit-preview">
            <img 
              :src="selectedOutfit.image" 
              :alt="selectedOutfit.name"
              class="selected-outfit-image"
            />
          </div>
          
          <button class="change-style-button" @click="openOutfitSwiper">
            Change Style
          </button>
          
          <div class="confirmation-arrow">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
        </div>
        
        <div class="confirmation-back-container">
          <button class="confirmation-back-btn" @click="closeOutfitSelector">
            Back to Customization
          </button>
        </div>
      </div>

      <!-- Outfit Swiper View -->
      <div v-else class="outfit-swiper-view">
        <div class="outfit-header">
          <h1 class="outfit-title">Animal Portrait Style</h1>
        </div>
        
        <!-- Style Toggle Buttons -->
        <div class="style-toggle-container">
          <button 
            v-for="style in outfitStyles" 
            :key="style"
            class="style-toggle-btn"
            :class="{ active: selectedOutfitStyle === style }"
            @click="selectOutfitStyle(style)"
          >
            {{ style }}
          </button>
        </div>
        
        <!-- Outfit Swiper -->
        <div class="outfit-swiper-container">
          <div class="swiper-container outfit-swiper">
            <div class="swiper-wrapper">
              <div v-for="(outfit, index) in currentOutfits" :key="index" class="swiper-slide">
                <div class="outfit-card" @click="selectOutfit(outfit)">
                  <div class="card-inner">
                    <div class="card-front">
                      <img 
                        :src="outfit.image" 
                        :alt="outfit.name"
                        class="outfit-image"
                      />
                    </div>
                    <div v-if="outfit.backImage" class="card-back">
                      <img 
                        :src="outfit.backImage" 
                        :alt="outfit.name + ' Back'"
                        class="outfit-image"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Navigation Arrows -->
        <div class="outfit-navigation">
          <button class="outfit-nav-btn left" @click="previousOutfit">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          
          <button class="outfit-nav-btn right" @click="nextOutfit">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
        
        <!-- Back Button -->
        <div class="outfit-back-container">
          <button class="outfit-back-btn" @click="closeOutfitSelector">
            Back to Customization
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MultiplePetsInput from '../MultiplePetsInput.vue';
import SocialMediaDisclaimer from '../SocialMediaDisclaimer.vue';
import RecommendedPhotoAngle from '../RecommendedPhotoAngle.vue';
import PetNameSelector from '../PetNameSelector.vue';
import Swiper from 'swiper';
import { EffectCoverflow, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/effect-coverflow';
import 'swiper/css/navigation';
import { uploadImage } from '@/firebase/storageService';
import { auth } from '@/firebase/init';

export default {
  name: 'ImageSelector',
  components: {
    MultiplePetsInput,
    SocialMediaDisclaimer,
    RecommendedPhotoAngle,
    PetNameSelector
  },
  props: {
    title: {
      type: String,
      default: 'select images'
    },
    imageList: {
      type: Array,
      default: () => [],
      validator: list => Array.isArray(list) && list.every(i => !!i.url)
    },
    maxSelection: {
      type: Number,
      default: 5,
      validator: v => v >= 1
    },
    minSelection: {
      type: Number,
      default: 1,
      validator: v => v >= 0
    },
    maxUpload: {
      type: Number,
      default: 5
    },
    name: {
      type: String,
      required: true
    },
    showed_image_amount: {
      type: Number,
      required: true
    },
    recommended_angle: {
      type: Array,
      default: () => []
    },
    not_recommended_angle: {
      type: Array,
      default: () => []
    },
    recommended_instruction: {
      type: String,
      default: ''
    },
    // NEW: Accept pre-selected images (for restoration from saved data)
    preSelectedImages: {
      type: Array,
      default: () => [],
      validator: list => Array.isArray(list)
    },
    // NEW: Accept pre-selected social media data
    preSelectedSocialMedia: {
      type: Object,
      default: () => null
    },
    // NEW: Accept pre-selected multiple pets data
    preSelectedMultiplePets: {
      type: Object,
      default: () => null
    },
    preSelectedOutfit: {
      type: Object,
      default: () => null
    }
  },
  data() {
    return {
      selectedIndices: [],
      uploadedImages: [],
      fullscreenImage: null,
      selectedOption: 'uploaded',
      selectedPlatform: '',
      socialUsername: '',
      showAdditionalCustomization: false,
      multiplePetsEnabled: false,
      portraitStyleEnabled: false,
      showMultiplePetsInput: false,
      multiplePetsList: [],
      showDisclaimer: false,
      disclaimerData: null,
      showOutfitSelector: false,
      selectedOutfitStyle: 'Modern',
      selectedGender: 'Female',
      outfitStyles: ['Modern', 'Medieval'],
      genders: ['Female'],
      currentStyleIndex: 0,
      outfitSwiper: null,
      selectedOutfit: null,
      showOutfitSwiper: false,
      modernOutfits: [],
      medievalOutfits: [],
      showRecommendedAngle: false,
      showPetNameSelection: false,
      selectedPetImage: '',

      isFirstTimeUpload: true,
      hasShownRecommendedAngle: false,
      selectedPetName: '',
      imageNameMappings: [], // Track which images have been named
      currentImageIndex: 0, // Track which image we're currently naming
      pendingUpload: null, // Store the current image being uploaded
      resolveNameSelection: null, // Promise resolver for name selection
      pendingPreSelectedOutfit: null,
      isRestoringOutfit: false,
      previousSocialAccounts: [],
      showPreviousAccounts: false,
    };
  },
  async mounted(){
      // Load outfits from scanned directory
      await this.loadOutfits();
      
      this.initCloudImage();
      // Set default selected outfit
      if (this.modernOutfits.length > 0) {
        this.selectedOutfit = this.modernOutfits[0];
      }
      
      // Load previous social media accounts
      const saved = localStorage.getItem('previous_social_accounts');
      if (saved) {
        try {
          this.previousSocialAccounts = JSON.parse(saved);
        } catch (e) {
          console.error('Error loading previous social accounts:', e);
        }
      }
      
      // Check if we should show recommended angle on initial load
      // Skip if ANY data exists (images OR social media)
      const hasRestoredImages = this.preSelectedImages && this.preSelectedImages.length > 0;
      const hasRestoredSocialMedia = this.preSelectedSocialMedia && this.preSelectedSocialMedia.platform;
      
      if (this.selectedOption === 'uploaded' && 
          this.isFirstTimeUpload && 
          !this.hasShownRecommendedAngle && 
          !hasRestoredImages && 
          !hasRestoredSocialMedia) {
        this.showRecommendedAngle = true;
      }
  },
  beforeUnmount() {
    window.removeEventListener('click', this.closePreviousAccounts);
  },
  watch: {
    preSelectedImages: {
      immediate: true,
      handler(newImages) {
        if (newImages && newImages.length > 0) {
          console.log('🔄 ImageSelector: Restoring pre-selected images:', newImages);
          console.log('  Current uploadedImages count:', this.uploadedImages.length);
          console.log('  Current combinedList count:', this.combinedList.length);
          
          // Add restored images to uploadedImages if not already there
          newImages.forEach(img => {
            const exists = this.uploadedImages.some(uploaded => uploaded.url === img.url);
            if (!exists) {
              this.uploadedImages.push(img);
              console.log('  ✅ Added restored image to uploadedImages:', img.url);
            } else {
              console.log('  ℹ️ Image already in uploadedImages:', img.url);
            }
          });
          
          console.log('  After adding - uploadedImages count:', this.uploadedImages.length);
          console.log('  After adding - combinedList count:', this.combinedList.length);
          
          // Select the restored images
          this.selectedIndices = [];
          newImages.forEach(img => {
            const index = this.combinedList.findIndex(item => item.url === img.url);
            console.log(`  Looking for image in combinedList: ${img.url.substring(0, 50)}... -> index: ${index}`);
            if (index >= 0 && !this.selectedIndices.includes(index)) {
              this.selectedIndices.push(index);
              console.log('  ✅ Selected index:', index);
            } else if (index < 0) {
              console.error('  ❌ Image not found in combinedList!');
            }
          });
          
          console.log(`  📦 Total: ${this.selectedIndices.length} images restored and selected`);
          console.log('  selectedIndices:', this.selectedIndices);
          console.log('  selectedImages:', this.selectedImages);
          
          // If multiple pets is enabled, assign pet names to images based on multiplePetsList
          if (this.multiplePetsEnabled && this.multiplePetsList.length > 0) {
            this.$nextTick(() => {
              console.log('  🐾 Assigning pet names to restored images...');
              console.log('  multiplePetsList:', this.multiplePetsList);
              
              // Create a new array to trigger Vue reactivity
              this.uploadedImages = this.uploadedImages.map((uploadedImg, index) => {
                // Find which pet this image belongs to by checking imageIndices
                const pet = this.multiplePetsList.find(p => {
                  if (!p.imageIndices || p.imageIndices.length === 0) return false;
                  return p.imageIndices.includes(index);
                });
                
                if (pet) {
                  console.log(`    ✅ Assigned "${pet.name}" to image at index ${index}`);
                  return { ...uploadedImg, petName: pet.name };
                } else {
                  console.log(`    ⚠️ No pet found for image at index ${index}`);
                  return uploadedImg;
                }
              });
            });
          }
        }
      }
    },
    preSelectedSocialMedia: {
      immediate: true,
      handler(socialData) {
        if (socialData) {
          console.log('🔄 ImageSelector: Restoring social media data:', socialData);
          
          // Only switch to social tab if no uploaded images exist
          // This allows both to coexist without forcing a tab switch
          if (this.uploadedImages.length === 0 && socialData.platform && socialData.username) {
            this.selectedOption = 'social';
          }
          
          this.selectedPlatform = socialData.platform || '';
          this.socialUsername = socialData.username || '';
          
          // Restore metadata
          if (socialData.multiplePets !== undefined) {
            this.multiplePetsEnabled = socialData.multiplePets;
          }
          if (socialData.multiplePetsList) {
            this.multiplePetsList = socialData.multiplePetsList;
          }
          if (socialData.portraitStyle !== undefined) {
            this.portraitStyleEnabled = socialData.portraitStyle;
          }
          
          console.log('  ✅ Social media restored:', {
            platform: this.selectedPlatform,
            username: this.socialUsername,
            multiplePets: this.multiplePetsEnabled,
            portraitStyle: this.portraitStyleEnabled
          });
          
          // Emit the restored data
          this.updateSocialData();
        }
      }
    },
    preSelectedMultiplePets: {
      immediate: true,
      handler(petsData) {
        if (petsData) {
          console.log('🔄 ImageSelector: Restoring multiple pets data:', petsData);
          
          if (petsData.multiplePets !== undefined) {
            this.multiplePetsEnabled = petsData.multiplePets;
          }
          if (petsData.multiplePetsList) {
            // Ensure each pet has imageIndices array initialized
            this.multiplePetsList = petsData.multiplePetsList.map(pet => ({
              ...pet,
              imageIndices: pet.imageIndices || []
            }));
          }
          if (petsData.portraitStyle !== undefined) {
            this.portraitStyleEnabled = petsData.portraitStyle;
          }
          
          console.log('  ✅ Multiple pets restored:', {
            enabled: this.multiplePetsEnabled,
            pets: this.multiplePetsList,
            portraitStyle: this.portraitStyleEnabled
          });
          
          // Assign pet names to already-uploaded images based on the restored multiplePetsList
          if (this.multiplePetsEnabled && this.multiplePetsList.length > 0 && this.uploadedImages.length > 0) {
            this.$nextTick(() => {
              console.log('  🐾 Assigning pet names to existing uploaded images...');
              console.log('  multiplePetsList:', JSON.parse(JSON.stringify(this.multiplePetsList)));
              
              // Create a new array to trigger Vue reactivity
              this.uploadedImages = this.uploadedImages.map((uploadedImg, index) => {
                console.log(`\n  🔍 Looking for pet for image index: ${index}`);
                
                // Find which pet this image belongs to by checking imageIndices
                const pet = this.multiplePetsList.find(p => {
                  if (!p.imageIndices || p.imageIndices.length === 0) return false;
                  
                  const match = p.imageIndices.includes(index);
                  if (match) {
                    console.log(`    ✅ Found pet "${p.name}" for index ${index}`);
                  }
                  return match;
                });
                
                if (pet) {
                  console.log(`    ✅ Assigned "${pet.name}" to image at index ${index}`);
                  return { ...uploadedImg, petName: pet.name };
                } else {
                  console.log(`    ⚠️ No pet found for image at index ${index}`);
                  return uploadedImg;
                }
              });
            });
          }
        }
      }
    },
    preSelectedOutfit: {
      immediate: true,
      handler(outfitData) {
        if (!outfitData) {
          this.pendingPreSelectedOutfit = null;
          return;
        }
        const applied = this.applyPreSelectedOutfit(outfitData);
        if (!applied) {
          this.pendingPreSelectedOutfit = outfitData;
        } else {
          this.pendingPreSelectedOutfit = null;
        }
      }
    },
    selectedOption(newVal) {
      if (newVal === 'uploaded') {
        // Show recommended angle on first time upload ONLY if no images exist
        if (this.isFirstTimeUpload && !this.hasShownRecommendedAngle && this.uploadedImages.length === 0) {
          this.showRecommendedAngle = true;
          return;
        }
        this.updateUploadedData();
      } else if (newVal === 'social') {
        this.updateSocialData();
      }
    },
    selectedIndices() {
      if (this.selectedOption === 'uploaded') {
        this.updateUploadedData();
      }
    },
    selectedPlatform(newVal, oldVal) {
      if (newVal && !oldVal && !this.disclaimerData) {
        // Show disclaimer when platform is first selected
        this.showDisclaimer = true;
      }
      this.updateSocialData();
    },
    socialUsername() {
      this.updateSocialData();
    },
    multiplePetsEnabled(newVal) {
      if (newVal) {
        // Reset image name mappings when enabling Multiple Pets
        this.imageNameMappings = [];
        this.currentImageIndex = 0;
        
        // If no pet names available, show pets input page to add pets first
        if (this.availablePetNames.length === 0) {
        this.showMultiplePetsInput = true;
          return;
        }
        
        // If user has uploaded images, require naming for existing images
        if (this.selectedOption === 'uploaded' && this.selectedImages.length > 0) {
          this.startNamingExistingImages();
          return;
        }
      } else {
        // When disabling Multiple Pets, clear all mappings
        this.imageNameMappings = [];
        this.currentImageIndex = 0;
      }
      if (this.selectedOption === 'social') {
        this.updateSocialData();
      } else {
        this.updateUploadedData();
      }
    },
    portraitStyleEnabled(newVal) {
      if (newVal) {
        if (!this.isRestoringOutfit) {
          this.openOutfitSelector();
        }
        this.saveOutfitSelection();
      }
      if (this.selectedOption === 'social') {
        this.updateSocialData();
      } else {
        this.updateUploadedData();
      }
    },
    showMultiplePetsInput(newVal) {
      if (!newVal) {
        // When returning from Multiple Pets page, reinitialize cloudimage-360
        this.$nextTick(() => {
          this.initCloudImage();
        });
      }
    },
    showAdditionalCustomization(newVal) {
      if (!newVal) {
        // When returning from Additional Customization, reinitialize cloudimage-360
        this.$nextTick(() => {
          this.initCloudImage();
        });
      }
    },
    showOutfitSelector(newVal) {
      if (newVal) {
        // When outfit selector is shown, initialize swiper if needed
        this.$nextTick(() => {
          if (this.showOutfitSwiper) {
            this.initOutfitSwiper();
          }
        });
      } else {
        // When outfit selector is hidden, destroy swiper
        if (this.outfitSwiper) {
          this.outfitSwiper.destroy(true, true);
          this.outfitSwiper = null;
        }
      }
    },
    showOutfitSwiper(newVal) {
      if (newVal) {
        // When swiper view is shown, initialize swiper
        this.$nextTick(() => {
          this.initOutfitSwiper();
        });
      } else {
        // When swiper view is hidden, destroy swiper
        if (this.outfitSwiper) {
          this.outfitSwiper.destroy(true, true);
          this.outfitSwiper = null;
        }
      }
    }
  },
  computed: {
    combinedList() {
      return [...this.imageList, ...this.uploadedImages];
    },
    selectedImages() {
      return this.selectedIndices.map(i => this.combinedList[i]);
    },
    currentOutfits() {
      if (this.selectedOutfitStyle === 'Modern') {
        return this.modernOutfits;
      } else if (this.selectedOutfitStyle === 'Medieval') {
        return this.medievalOutfits;
      }
      return [];
    },
    availablePetNames() {
      // Extract pet names from multiplePetsList
      return this.multiplePetsList.map(pet => pet.name);
    }
  },
  methods: {
    saveSocialAccount() {
      if (this.selectedPlatform && this.socialUsername) {
        const newAccount = {
          platform: this.selectedPlatform,
          username: this.socialUsername
        };
        
        // Remove duplicate if exists
        const filtered = this.previousSocialAccounts.filter(acc => 
          !(acc.platform === newAccount.platform && acc.username === newAccount.username)
        );
        
        // Add to front and limit to 5
        const updated = [newAccount, ...filtered].slice(0, 5);
        this.previousSocialAccounts = updated;
        localStorage.setItem('previous_social_accounts', JSON.stringify(updated));
      }
    },
    selectPreviousAccount(account) {
      // Set disclaimerData before setting platform to avoid triggering disclaimer
      this.disclaimerData = { 
        accepted: true, 
        isPreviousAccount: true,
        timestamp: new Date().toISOString()
      };
      this.selectedPlatform = account.platform;
      this.socialUsername = account.username;
      this.showPreviousAccounts = false;
    },
    togglePreviousAccounts() {
      this.showPreviousAccounts = !this.showPreviousAccounts;
      if (this.showPreviousAccounts) {
        setTimeout(() => {
          window.addEventListener('click', this.closePreviousAccounts);
        }, 0);
      } else {
        window.removeEventListener('click', this.closePreviousAccounts);
      }
    },
    closePreviousAccounts(e) {
      if (this.showPreviousAccounts) {
        const dropdown = this.$el.querySelector('.previous-accounts-dropdown');
        const historyBtn = this.$el.querySelector('.history-button');
        
        if (dropdown && !dropdown.contains(e.target) && historyBtn && !historyBtn.contains(e.target)) {
          this.showPreviousAccounts = false;
          window.removeEventListener('click', this.closePreviousAccounts);
        }
      }
    },
    processOutfits(outfits) {
      // 分离正面和反面图片
      const frontOutfits = [];
      const backOutfitsMap = {};
      
      outfits.forEach(outfit => {
        if (outfit.name.endsWith(' Back')) {
          // 移除 " Back" 后缀以获取基础名称
          const baseName = outfit.name.replace(' Back', '');
          backOutfitsMap[baseName] = outfit;
        } else {
          frontOutfits.push(outfit);
        }
      });
      
      // 将反面图片关联到正面图片
      return frontOutfits.map(front => {
        const back = backOutfitsMap[front.name];
        return {
          ...front,
          backImage: back ? back.image : null
        };
      });
    },
    async loadOutfits() {
      try {
        // 加载扫描生成的配置文件
        const response = await fetch('/data/outfits.json');
        if (!response.ok) {
          throw new Error('Failed to load outfits.json');
        }
        const data = await response.json();
        
        // 更新服装列表并处理正反面
        this.modernOutfits = this.processOutfits(data.modern || []);
        this.medievalOutfits = this.processOutfits(data.medieval || []);
        
        console.log(`✅ Loaded ${this.modernOutfits.length} modern outfits and ${this.medievalOutfits.length} medieval outfits`);

        if (this.pendingPreSelectedOutfit) {
          const applied = this.applyPreSelectedOutfit(this.pendingPreSelectedOutfit);
          if (applied) {
            this.pendingPreSelectedOutfit = null;
          }
        }
      } catch (error) {
        console.warn('⚠️ Failed to load outfits.json, using empty arrays:', error);
        // 如果加载失败，保持空数组
        this.modernOutfits = [];
        this.medievalOutfits = [];
      }
    },
    updateSocialData() {
      if (this.selectedOption === 'social') {
        const socialData = {
          type: 'social',
          platform: this.selectedPlatform,
          username: this.socialUsername,
          multiplePets: this.multiplePetsEnabled,
          multiplePetsList: this.multiplePetsList,
          portraitStyle: this.portraitStyleEnabled,
          disclaimerData: this.disclaimerData
        };

        if (this.selectedOutfit) {
          socialData.selectedOutfitName = this.selectedOutfit.name;
          socialData.selectedOutfitStyle = this.selectedOutfitStyle;
        }

        console.log('📱 ImageSelector emitting social media data:', socialData);
        this.$emit('updateSelection', socialData);
      }
    },
    updateUploadedData() {
      if (this.selectedOption === 'uploaded') {
        const data = {
          type: 'uploaded',
          images: this.selectedImages,
          multiplePets: this.multiplePetsEnabled,
          multiplePetsList: this.multiplePetsList,
          portraitStyle: this.portraitStyleEnabled
        };
        
        if (this.selectedOutfit) {
          data.selectedOutfitName = this.selectedOutfit.name;
          data.selectedOutfitStyle = this.selectedOutfitStyle;
        }
        
        this.$emit('updateSelection', data);
      }
    },
    handleDisclaimerAccepted(data) {
      this.disclaimerData = data;
      this.showDisclaimer = false;
      // Platform selection is confirmed
      // Reinitialize cloudimage-360 when returning from disclaimer
      this.$nextTick(() => {
        this.initCloudImage();
      });
    },
    handleDisclaimerRejected(reason) {
      this.showDisclaimer = false;
      // Reset platform selection and switch to upload image
      this.selectedPlatform = '';
      this.socialUsername = '';
      this.disclaimerData = null;
      this.selectedOption = 'uploaded'; // Switch to upload image mode
      // Don't show alert, the Access Notice already explains the situation
      // Reinitialize cloudimage-360 when returning from disclaimer
      this.$nextTick(() => {
        this.initCloudImage();
      });
    },
    handlePortraitStyle() {
      this.showAdditionalCustomization = true;
    },
    handleBackFromCustomization() {
      this.showAdditionalCustomization = false;
      // Reinitialize cloudimage-360 when returning from customization
      this.$nextTick(() => {
        this.initCloudImage();
      });
    },
    openOutfitSwiper() {
      this.showOutfitSwiper = true;
      this.showOutfitSelector = true;
    },
    openOutfitSelector() {
      this.showOutfitSelector = true;
      // If no outfit is selected, go directly to swiper
      if (!this.selectedOutfit) {
        this.showOutfitSwiper = true;
      }
    },
    handleMultiplePetsBack() {
      this.showMultiplePetsInput = false;
      // If no pets were added, disable the toggle
      if (this.multiplePetsList.length === 0) {
        this.multiplePetsEnabled = false;
      }
      // Reinitialize cloudimage-360 when returning from Multiple Pets
      this.$nextTick(() => {
        this.initCloudImage();
      });
    },
    handleMultiplePetsProceed(pets) {
      this.multiplePetsList = pets;
      this.showMultiplePetsInput = false;
      
      // If user has uploaded images, start naming process for existing images
      if (this.selectedOption === 'uploaded' && this.selectedImages.length > 0 && pets.length > 0) {
        this.startNamingExistingImages();
        return;
      }
      
      // Call the appropriate update method based on selected option
      if (this.selectedOption === 'social') {
        this.updateSocialData();
      } else {
        this.updateUploadedData();
      }
      // Reinitialize cloudimage-360 when returning from Multiple Pets
      this.$nextTick(() => {
        this.initCloudImage();
      });
    },
    handlePetsUpdated(pets) {
      this.multiplePetsList = pets;
    },
    toggleSelection(index) {
      const position = this.selectedIndices.indexOf(index);
      if (position === -1) {
        if (this.selectedIndices.length >= this.maxSelection) {
            alert(`You can select up to ${this.maxSelection} images`);
          return;
        }
        this.selectedIndices = [...this.selectedIndices, index];
      } else {
        this.selectedIndices = this.selectedIndices.filter(i => i !== index);
      }
      // Use the appropriate update method based on selected option
      if (this.selectedOption === 'uploaded') {
        this.updateUploadedData();
      } else {
        this.updateSocialData();
      }
    },
    isSelected(index) {
      return this.selectedIndices.includes(index);
    },
    triggerUpload() {
      this.$refs.fileInput.click();
    },
    async handleFileUpload(event) {
      const files = Array.from(event.target.files || []);
      if (files.length + this.uploadedImages.length > this.maxUpload) {
        alert(`You can upload up to ${this.maxUpload} images`);
        event.target.value = '';
        return;
      }

      // If Multiple Pets is enabled but no pet names available, show error
      if (this.multiplePetsEnabled && this.availablePetNames.length === 0) {
        alert('Please add pet names first before uploading images');
        event.target.value = '';
        return;
      }

      // Process files one by one if Multiple Pets is enabled
      if (this.multiplePetsEnabled) {
        await this.processFilesOneByOne(files);
      } else {
        // Process all files at once if Multiple Pets is disabled
        await this.processAllFiles(files);
      }
      
      event.target.value = '';
    },

    async processFilesOneByOne(files) {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        try {
          // Generate thumbnail for IMMEDIATE preview (UX first!)
          const thumbnail = await this.generateThumbnail(file);
          const localPreviewUrl = URL.createObjectURL(file);
          
          // Show image IMMEDIATELY in UI
          const uploadedImage = {
            url: localPreviewUrl, // Local preview URL for immediate display
            thumbnail, // Local thumbnail
            name: file.name,
            file: file, // Keep file reference for upload
            uploading: true, // Flag to show upload status
            firebaseUrl: null // Will be set after upload
          };

          // Store the pending upload and show immediately
          this.pendingUpload = uploadedImage;
          this.selectedPetImage = localPreviewUrl;
          
          // Upload to Firebase in BACKGROUND
          const user = auth.currentUser;
          if (user) {
            // User is logged in - upload to Firebase
            const journeyId = (this.name && this.name.trim()) || 'item-customization';
            
            uploadImage(file, user.uid, journeyId)
              .then(firebaseUrl => {
                // Update with Firebase URL when upload completes
                uploadedImage.firebaseUrl = firebaseUrl;
                uploadedImage.url = firebaseUrl;
                uploadedImage.uploading = false;
                this.selectedPetImage = firebaseUrl;
                console.log('Image uploaded to Firebase:', firebaseUrl);
              })
              .catch(error => {
                console.error('Firebase upload failed:', error);
                uploadedImage.uploading = false;
                uploadedImage.uploadError = true;
                // Keep local preview URL if upload fails
              });
          } else {
            // User not logged in - keep local only
            uploadedImage.uploading = false;
            uploadedImage.localOnly = true;
            console.log('Image stored locally (user not logged in)');
          }
          
          // Show name selection modal and wait for user to select a name
          this.showPetNameSelection = true;
          
          // Wait for name selection to complete
          await new Promise((resolve) => {
            this.resolveNameSelection = resolve;
          });

        } catch (error) {
          console.error('Error uploading file:', error);
          alert(`Failed to upload file: ${file.name}. Please try again.`);
        }
      }
    },

    async processAllFiles(files) {
      // Generate thumbnails and show IMMEDIATELY (UX first!)
      const previews = await Promise.all(
        files.map(async file => {
          const thumbnail = await this.generateThumbnail(file);
          const localPreviewUrl = URL.createObjectURL(file);
          
          return {
            url: localPreviewUrl, // Local preview for immediate display
            thumbnail,
            name: file.name,
            file: file,
            uploading: true,
            firebaseUrl: null,
            localOnly: false
          };
        })
      );
      
      // Show images IMMEDIATELY in UI
      const oldLength = this.uploadedImages.length;
      this.uploadedImages = [...this.uploadedImages, ...previews];
      
      // Auto-select newly uploaded images
      const newImageIndices = [];
      for (let i = 0; i < previews.length; i++) {
        const newIndex = this.imageList.length + oldLength + i;
        newImageIndices.push(newIndex);
      }
      
      const availableSlots = this.maxSelection - this.selectedIndices.length;
      const indicesToAdd = newImageIndices.slice(0, availableSlots);
      this.selectedIndices = [...this.selectedIndices, ...indicesToAdd];
      
      // Upload to Firebase in BACKGROUND
      const user = auth.currentUser;
      if (user) {
        const journeyId = (this.name && this.name.trim()) || 'item-customization';
        
        // Upload each file in background
        previews.forEach((preview, index) => {
          uploadImage(preview.file, user.uid, journeyId)
            .then(firebaseUrl => {
              preview.firebaseUrl = firebaseUrl;
              preview.url = firebaseUrl;
              preview.uploading = false;
              console.log(`Image ${index + 1}/${previews.length} uploaded to Firebase`);
            })
            .catch(error => {
              console.error('Firebase upload failed:', preview.name, error);
              preview.uploading = false;
              preview.uploadError = true;
            });
        });
      } else {
        // User not logged in - mark as local only
        previews.forEach(preview => {
          preview.uploading = false;
          preview.localOnly = true;
        });
        console.log('Images stored locally (user not logged in)');
      }
      
      console.log(`Processing ${previews.length} images (showing immediately, uploading in background)`);
    },
    convertToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
      });
    },
    generateThumbnail(file) {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          canvas.width = 200;
          canvas.height = 200;
          ctx.drawImage(img, 0, 0, 200, 200);
          resolve(canvas.toDataURL('image/jpeg'));
        };
        img.src = URL.createObjectURL(file);
      });
    },
    handleCancel() {
      this.selectedIndices = [];
      this.uploadedImages = [];
      this.showAdditionalCustomization = false;
      this.showMultiplePetsInput = false;
      this.showDisclaimer = false;
      this.showRecommendedAngle = false;
      this.showPetNameSelection = false;
      this.isFirstTimeUpload = true;
      this.hasShownRecommendedAngle = false;
      this.selectedPetName = '';
      this.selectedPetImage = '';
      this.imageNameMappings = [];
      this.currentImageIndex = 0;
      this.pendingUpload = null;
      this.resolveNameSelection = null;
      this.$emit('cancel');
    },
    handleConfirm() {
      const selected = this.selectedImages.map(img => ({
        original: img.url,
        thumbnail: img.thumbnail
      }));
      this.$emit('confirm', selected);
    },
    showFullscreen(url) {
      this.fullscreenImage = url;
    },
    next() {
      if (this.selectedOption === 'social') {
        if (!this.selectedPlatform || !this.socialUsername) {
          alert('Please select a platform and enter your username');
          return;
        }
        this.saveSocialAccount();
      } else if (this.selectedOption === 'uploaded') {
        if (this.selectedImages.length < this.minSelection) {
          alert(`Please upload and select at least ${this.minSelection} image${this.minSelection > 1 ? 's' : ''} to proceed`);
          return;
        }
      }
      this.$emit('next', "nextProduct");
    },
    initCloudImage() {
      // Check if CI360 is available and the element exists
      if (window.CI360) {
        // Wait for DOM to be ready
        this.$nextTick(() => {
          const cloudImageElement = document.getElementById('gurkha-suv');
          if (cloudImageElement) {
            try {
              // Destroy existing instances first
              if (window.CI360.destroy) {
                window.CI360.destroy();
              }
              // Small delay to ensure DOM is fully rendered
              setTimeout(() => {
                try {
                  window.CI360.init();
                  console.log('CloudImage 360 initialized successfully');
                } catch (retryError) {
                  console.warn('CloudImage 360 retry initialization failed:', retryError);
                }
              }, 50);
            } catch (error) {
              console.warn('CloudImage 360 initialization failed:', error);
              // Fallback: try to initialize after a longer delay
              setTimeout(() => {
                try {
                  window.CI360.init();
                } catch (retryError) {
                  console.warn('CloudImage 360 fallback initialization failed:', retryError);
                }
              }, 200);
            }
          } else {
            console.warn('CloudImage 360 element not found, retrying...');
            // Retry if element not found
            setTimeout(() => {
              this.initCloudImage();
            }, 100);
          }
        });
      }
    },
    getOutfitImage(style, gender) {
      // Return placeholder image URL based on the selected style
      // In a real implementation, this would return actual outfit images
      return `/assets/images/outfits/${style.toLowerCase()}_${gender.toLowerCase()}.jpg`;
    },
    getOutfitThumbnail(style, thumbnail) {
      // Return placeholder image URL based on the selected style
      // In a real implementation, this would return actual outfit thumbnails
      return `/assets/images/outfits/${style.toLowerCase()}_${thumbnail}.jpg`;
    },
    selectOutfitStyle(style) {
      this.selectedOutfitStyle = style;
      // Reinitialize swiper when style changes
      this.$nextTick(() => {
        if (this.outfitSwiper) {
          this.outfitSwiper.destroy(true, true);
        }
        this.initOutfitSwiper();
      });
    },
    previousOutfit() {
      if (this.outfitSwiper) {
        this.outfitSwiper.slidePrev();
      }
    },
    nextOutfit() {
      if (this.outfitSwiper) {
        this.outfitSwiper.slideNext();
      }
    },
    updateOutfitZIndex(swiper) {
      if (!swiper || !swiper.slides) {
        return;
      }

      const { slides } = swiper;
      for (let i = 0; i < slides.length; i += 1) {
        const slide = slides[i];
        const progress = typeof slide.progress === 'number' ? slide.progress : 0;
        const absProgress = Math.abs(progress);

        let zIndex = 100;
        if (absProgress < 0.1) {
          zIndex = 1000;
        } else if (absProgress < 1.1) {
          zIndex = 500;
        } else {
          zIndex = Math.max(10, 500 - Math.floor(absProgress * 100));
        }
        slide.style.zIndex = zIndex;

        const opacity = absProgress > 0.1 ? Math.max(0.6, 1 - (absProgress * 0.3)) : 1;
        slide.style.opacity = opacity;
      }
    },
    initOutfitSwiper() {
      const swiperElement = document.querySelector('.outfit-swiper');
      if (swiperElement) {
        this.outfitSwiper = new Swiper('.outfit-swiper', {
          modules: [EffectCoverflow, Navigation],
          effect: 'coverflow',
          grabCursor: true,
          centeredSlides: true,
          slidesPerView: 'auto',
          loop: true,
          spaceBetween: 30,
          speed: 800,
          slideToClickedSlide: true,
          watchSlidesProgress: true,
          coverflowEffect: {
            rotate: 0,
            stretch: 0,
            depth: 150,
            modifier: 1.5,
            slideShadows: false,
          },
          navigation: {
            nextEl: '.outfit-nav-btn.right',
            prevEl: '.outfit-nav-btn.left',
          },
          on: {
            init: (swiper) => {
              this.updateOutfitZIndex(swiper);
              requestAnimationFrame(() => this.updateOutfitZIndex(swiper));
            },
            resize: (swiper) => this.updateOutfitZIndex(swiper),
            setTranslate: (swiper) => this.updateOutfitZIndex(swiper),
            setTransition: (swiper, duration) => {
              const { slides } = swiper;
              for (let i = 0; i < slides.length; i += 1) {
                slides[i].style.transitionDuration = `${duration}ms`;
              }
            },
          }
        });
      }
    },
    selectOutfit(outfit) {
      this.selectedOutfit = outfit;
      this.showOutfitSwiper = false;
    },
    saveOutfitSelection() {
      if (!this.selectedOutfit) {
        return;
      }
      this.updateUploadedData();
      this.updateSocialData();
    },
    async closeOutfitSelector() {
      this.showOutfitSelector = false;
      this.showOutfitSwiper = false;

      // Save selected outfit name to Firebase
      this.saveOutfitSelection();
    },
    findOutfitByName(name, style) {
      if (!name) return null;
      let source = [];
      if (style === 'Modern') {
        source = this.modernOutfits;
      } else if (style === 'Medieval') {
        source = this.medievalOutfits;
      } else {
        source = [...this.modernOutfits, ...this.medievalOutfits];
      }
      return source.find(outfit => outfit.name === name) || null;
    },
    applyPreSelectedOutfit(outfitData) {
      if (!outfitData) {
        return false;
      }
      const name = outfitData.selectedOutfitName || outfitData.name;
      const style = outfitData.selectedOutfitStyle || outfitData.style;
      const portraitStylePreference = outfitData.portraitStyle;
      const shouldEnablePortraitStyle = typeof portraitStylePreference === 'boolean'
        ? portraitStylePreference
        : (typeof outfitData.portraitStyleEnabled === 'boolean' ? outfitData.portraitStyleEnabled : this.portraitStyleEnabled);

      if (style) {
        this.selectedOutfitStyle = style;
      }

      const matchingOutfit = this.findOutfitByName(name, style);
      if (!matchingOutfit) {
        return false;
      }

      this.selectedOutfit = matchingOutfit;

      const finalizeSelection = () => {
        this.saveOutfitSelection();
      };

      if (shouldEnablePortraitStyle) {
        if (this.portraitStyleEnabled) {
          finalizeSelection();
        } else {
          this.isRestoringOutfit = true;
          this.portraitStyleEnabled = true;
          this.$nextTick(() => {
            this.isRestoringOutfit = false;
            finalizeSelection();
          });
        }
      } else {
        // ensure toggle reflects disabled state without reopening modal
        if (this.portraitStyleEnabled) {
          this.isRestoringOutfit = true;
          this.portraitStyleEnabled = false;
          this.$nextTick(() => {
            this.isRestoringOutfit = false;
          });
        }
      }

      return true;
    },

     startNamingExistingImages() {
       // Reset naming process for existing images
       this.imageNameMappings = [];
       this.currentImageIndex = 0;
       this.pendingUpload = null; // Clear any pending upload
       
       // Start with the first selected image
       if (this.selectedImages.length > 0 && this.selectedImages[0] && this.selectedImages[0].url) {
         this.selectedPetImage = this.selectedImages[0].url;
         this.showPetNameSelection = true;
       }
     },
         handleRecommendedAngleProceed() {
       this.showRecommendedAngle = false;
       this.hasShownRecommendedAngle = true;
       this.isFirstTimeUpload = false;
       // Reinitialize cloudimage-360 when returning from recommended angle
       this.$nextTick(() => {
         this.initCloudImage();
       });
     },
     handleRecommendedAngleSkip() {
       this.showRecommendedAngle = false;
       this.hasShownRecommendedAngle = true;
       this.isFirstTimeUpload = false;
       // Reinitialize cloudimage-360 when returning from recommended angle
       this.$nextTick(() => {
         this.initCloudImage();
       });
     },
     handleModalBackdropClick() {
       // Close modal when clicking on backdrop (optional behavior)
       this.handleRecommendedAngleSkip();
     },
     handlePetNameModalBackdropClick() {
       // Close pet name modal when clicking on backdrop
       this.handlePetNameBack();
     },
          handlePetNameSelected(name) {
       this.selectedPetName = name;
       
       // If we have a pending upload (single file upload mode)
       if (this.pendingUpload) {
         // Add the named image to uploaded images
         const namedImage = {
           ...this.pendingUpload,
           petName: name
         };
         
         // Add to uploaded images
         const oldLength = this.uploadedImages.length;
         this.uploadedImages = [...this.uploadedImages, namedImage];
         
         // Auto-select the newly uploaded image
         const newIndex = this.imageList.length + oldLength;
         if (this.selectedIndices.length < this.maxSelection) {
           this.selectedIndices = [...this.selectedIndices, newIndex];
         }
         
         // Save the image-name mapping
         this.imageNameMappings.push({
           imageUrl: this.pendingUpload.url,
           imageIndex: newIndex,
           petName: name
         });
         
         // Associate the selected name with the current image
         const petIndex = this.multiplePetsList.findIndex(pet => pet.name === name);
         if (petIndex !== -1) {
           // Initialize imageIndices array if it doesn't exist
           if (!this.multiplePetsList[petIndex].imageIndices) {
             this.multiplePetsList[petIndex].imageIndices = [];
           }
           // Add image index to this pet's imageIndices array
           if (!this.multiplePetsList[petIndex].imageIndices.includes(newIndex)) {
             this.multiplePetsList[petIndex].imageIndices.push(newIndex);
           }
           this.multiplePetsList[petIndex] = {
             ...this.multiplePetsList[petIndex],
             hasImage: true
           };
         }
         
         // Clear pending upload
         this.pendingUpload = null;
         this.showPetNameSelection = false;
         
         // Resolve the promise to continue with next file
         if (this.resolveNameSelection) {
           this.resolveNameSelection();
           this.resolveNameSelection = null;
         }
       } else {
         // Legacy batch mode (for existing uploaded images)
         this.handleBatchNameSelection(name);
       }
       
       // Reinitialize cloudimage-360 when returning from pet name selection
       this.$nextTick(() => {
         this.initCloudImage();
       });
       // Update data based on current selection type
       if (this.selectedOption === 'social') {
         this.updateSocialData();
       } else {
         this.updateUploadedData();
       }
     },

     handleBatchNameSelection(name) {
       // Save the current image-name mapping
       this.imageNameMappings.push({
         imageUrl: this.selectedPetImage,
         imageIndex: this.currentImageIndex,
         petName: name
       });
       
       // Associate the selected name with the current image
       const petIndex = this.multiplePetsList.findIndex(pet => pet.name === name);
       if (petIndex !== -1) {
         // Initialize imageIndices array if it doesn't exist
         if (!this.multiplePetsList[petIndex].imageIndices) {
           this.multiplePetsList[petIndex].imageIndices = [];
         }
         // Add image index to this pet's imageIndices array
         if (!this.multiplePetsList[petIndex].imageIndices.includes(this.currentImageIndex)) {
           this.multiplePetsList[petIndex].imageIndices.push(this.currentImageIndex);
         }
         this.multiplePetsList[petIndex] = {
           ...this.multiplePetsList[petIndex],
           hasImage: true
         };
       }
       
       // Add petName to the actual uploaded image object
       const currentImage = this.selectedImages[this.currentImageIndex];
       if (currentImage) {
         // Find the corresponding image in uploadedImages and add petName
         const uploadedIndex = this.uploadedImages.findIndex(img => img.url === currentImage.url);
         if (uploadedIndex !== -1) {
           this.uploadedImages[uploadedIndex] = {
             ...this.uploadedImages[uploadedIndex],
             petName: name
           };
         }
       }
       
       // Move to next image if there are more images to name
       this.currentImageIndex++;
       if (this.currentImageIndex < this.selectedImages.length) {
         const nextImage = this.selectedImages[this.currentImageIndex];
         if (nextImage && nextImage.url) {
           // Show next image for naming
           this.selectedPetImage = nextImage.url;
           // Keep the modal open for next image
           return;
         }
       }
       
       // All images have been named, close the modal
       this.showPetNameSelection = false;
       this.currentImageIndex = 0;
       
       // Show completion message
       alert(`All ${this.selectedImages.length} images have been assigned pet names successfully!`);
     },
     handlePetNameBack() {
       this.showPetNameSelection = false;
       
       // If we have a pending upload, this means user cancelled the upload
       if (this.pendingUpload) {
         // Clear pending upload (this effectively cancels the upload)
         this.pendingUpload = null;
         this.selectedPetImage = '';
         
         // Reject the promise to stop processing more files
         if (this.resolveNameSelection) {
           this.resolveNameSelection();
           this.resolveNameSelection = null;
         }
       } else if (this.selectedImages.length > 0) {
         // User cancelled naming existing images - disable Multiple Pets
         const confirmDisable = confirm(
           'You must assign pet names to all uploaded images when Multiple Pets is enabled. ' +
           'Do you want to disable Multiple Pets mode instead?'
         );
         
         if (confirmDisable) {
           // Disable Multiple Pets mode
           this.multiplePetsEnabled = false;
           this.imageNameMappings = [];
           this.currentImageIndex = 0;
           this.selectedPetImage = '';
         } else {
           // User wants to continue naming, restart the process
           this.startNamingExistingImages();
           return;
         }
       } else {
         // Legacy batch mode - reset naming process
         this.imageNameMappings = [];
         this.currentImageIndex = 0;
         this.selectedPetImage = '';
       }
       
       // Reinitialize cloudimage-360 when returning from pet name selection
       this.$nextTick(() => {
         this.initCloudImage();
       });
     },
  }
};
</script>

<style scoped>
.multiple-pets-page {
  width: 100%;
  min-height: 400px;
}

/* Recommended Photo Angle Modal Styles */
.recommended-angle-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 1vw;
  padding: 2vw;
  max-width: 90vw;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 0.5vw 2vw rgba(0, 0, 0, 0.2);
  animation: modalFadeIn 0.3s ease-out;
}

@keyframes modalFadeIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Recommended Angle Trigger Styles */
.recommended-angle-trigger-container {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 0.5vw;
}

.recommended-angle-hint-btn {
  display: flex;
  align-items: center;
  gap: 0.5vw;
  background: white;
  border: 0.1vw solid #eee;
  border-radius: 2vw;
  padding: 0.4vw 1vw;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #666;
  font-size: 0.9vw;
  font-weight: 600;
  box-shadow: 0 0.1vw 0.4vw rgba(0,0,0,0.05);
  font-family: inherit;
}

.recommended-angle-hint-btn:hover {
  background: #f8f9fa;
  border-color: #ddd;
  color: #333;
  transform: translateY(-0.1vw);
  box-shadow: 0 0.2vw 0.6vw rgba(0,0,0,0.1);
}

.recommended-angle-hint-btn svg {
  color: #f1c40f;
  flex-shrink: 0;
}

/* Pet Name Selection Modal Styles */
.pet-name-selection-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
}

.image-container{
  display: flex;
  justify-content: center; /* 水平居中 */
  align-items: center; /* 垂直居中 */
  margin-top: 4.15vw;
  border-radius: 1.6667vw;
  width: 25vw;
  height: 25vw;
  overflow: hidden;
}
.content-top{
  margin-top: 5.5vw;
  position: relative;
  left: 50%;
  transform: translateX(-50%);
  height: 2.15vw;
  font-size: 2.75vw;
  font-family: BaskervilleDisplayPT, BaskervilleDisplayPT-Bold;
  font-weight: 700;
  text-align: center;
  color: #514f4c;
}
.image-selector-container {
  --primary-color: #2196f3;
  --spacing: 0.8vw;
  max-width: 60vw;
  margin: 0 auto;
  padding: var(--spacing);
}

.main-preview-area {
  margin-bottom: 0.1vw;
  border: 0.05vw solid #eee;
  border-radius: 0.4vw;
  overflow: hidden;
  background: #f8f9fa;
  min-height: 13vw;
}

.selected-preview {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(7.5vw, 1fr));
  gap: 0.8vw;
  padding: 0.8vw;
}

.main-preview-image {
  width: 100%;
  height: 13vw;
  object-fit: contain;
  border-radius: 0.2vw;
  box-shadow: 0 0.1vw 0.4vw rgba(0,0,0,0.1);
  transition: transform 0.3s ease;
  cursor: zoom-in;
}

.empty-prompt {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 13vw;
  color: #666;
  font-size: 1vw;
}

/* Social Media Styles */
.social-media-area {
  margin-bottom: 0.1vw;
  min-height: 13vw;
  display: flex;
  align-items: center;
  justify-content: center;
}

.social-media-content {
  width: 100%;
  padding: 1.5vw;
}

.social-media-title {
  font-size: 1.2vw;
  color: #333;
  text-align: center;
  margin-bottom: 1.5vw;
  line-height: 1.5;
}

.form-group {
  margin-bottom: 1vw;
}

.dropdown-wrapper {
  position: relative;
  width: 100%;
}

.platform-select {
  width: 100%;
  padding: 0.8vw 2.5vw 0.8vw 0.8vw;
  font-size: 1vw;
  border: 0.1vw solid #ddd;
  border-radius: 0.3vw;
  background: white;
  appearance: none;
  cursor: pointer;
  color: #333;
  transition: border-color 0.3s;
}

.platform-select:focus {
  outline: none;
  border-color: #666;
}

.dropdown-icon {
  position: absolute;
  right: 0.8vw;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  width: 1.2vw;
  height: 1.2vw;
}

.input-wrapper {
  position: relative;
  width: 100%;
}

.input-icon {
  position: absolute;
  left: 0.8vw;
  top: 50%;
  transform: translateY(-50%);
  font-size: 1.2vw;
}

.username-input {
  width: 100%;
  padding: 0.8vw 3vw 0.8vw 2.5vw;
  font-size: 1vw;
  border: 0.1vw solid #ddd;
  border-radius: 0.3vw;
  transition: border-color 0.3s;
}

.username-input:focus {
  outline: none;
  border-color: #666;
}

.username-input::placeholder {
  color: #999;
}

.history-button {
  position: absolute;
  right: 0.8vw;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.2vw;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  border-radius: 50%;
}

.history-button:hover {
  color: #333;
  background-color: rgba(0, 0, 0, 0.05);
}

.history-button.active {
  color: #ff4d8d;
  background-color: rgba(255, 77, 141, 0.1);
}

.previous-accounts-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 0.1vw solid #eee;
  border-radius: 0.5vw;
  box-shadow: 0 0.5vw 1.5vw rgba(0,0,0,0.1);
  z-index: 100;
  margin-top: 0.5vw;
  overflow: hidden;
}

.dropdown-header {
  padding: 0.8vw 1.2vw;
  background: #f9f9f9;
  border-bottom: 0.1vw solid #eee;
  font-size: 0.8vw;
  color: #666;
  font-weight: 600;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.close-dropdown {
  background: none;
  border: none;
  font-size: 1.2vw;
  cursor: pointer;
  color: #999;
  padding: 0;
  line-height: 1;
}

.dropdown-list {
  max-height: 15vw;
  overflow-y: auto;
}

.previous-account-item {
  padding: 1vw 1.2vw;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: background 0.2s;
  border-bottom: 0.1vw solid #f5f5f5;
}

.previous-account-item:last-child {
  border-bottom: none;
}

.previous-account-item:hover {
  background: #fff5f8;
}

.acc-info {
  display: flex;
  flex-direction: column;
  gap: 0.2vw;
}

.acc-platform {
  font-size: 0.7vw;
  text-transform: uppercase;
  color: #999;
  letter-spacing: 0.05vw;
}

.acc-username {
  font-size: 1vw;
  color: #333;
  font-weight: 500;
}

.select-hint {
  font-size: 0.8vw;
  color: #ff4d8d;
  font-weight: 600;
  opacity: 0;
  transition: opacity 0.2s;
}

.previous-account-item:hover .select-hint {
  opacity: 1;
}

.style-link {
  text-align: center;
  margin-top: 1.2vw;
}

.style-link a {
  color: #666;
  text-decoration: underline;
  font-size: 0.9vw;
  transition: color 0.3s;
}

.style-link a:hover {
  color: #333;
}

/* Additional Customization Styles */
.additional-customization-area {
  margin-bottom: 0.1vw;
  min-height: 13vw;
  background: #f8f9fa;
  border-radius: 0.4vw;
  padding: 1.5vw;
}

.additional-customization-content {
  width: 100%;
}

.additional-title {
  font-size: 1.5vw;
  font-weight: 600;
  color: #333;
  text-align: center;
  margin-bottom: 1.5vw;
}

.customization-option {
  background: white;
  border-radius: 0.4vw;
  padding: 1vw;
  margin-bottom: 1vw;
}

.option-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5vw;
}

.option-title {
  font-size: 1.1vw;
  font-weight: 500;
  color: #333;
}

.option-description {
  font-size: 0.9vw;
  color: #666;
  line-height: 1.5;
  margin: 0;
  text-align: left;
}

/* Toggle Switch Styles */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 2.5vw;
  height: 1.4vw;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: .4s;
  border-radius: 1.4vw;
}

.toggle-slider:before {
  position: absolute;
  content: "";
  height: 1vw;
  width: 1vw;
  left: 0.2vw;
  bottom: 0.2vw;
  background-color: white;
  transition: .4s;
  border-radius: 50%;
}

input:checked + .toggle-slider {
  background-color: #4CAF50;
}

input:checked + .toggle-slider:before {
  transform: translateX(1.1vw);
}

.view-outfit-link {
  display: inline-block;
  font-size: 0.9vw;
  color: #2196f3;
  text-decoration: underline;
  margin-bottom: 0.5vw;
}

.view-outfit-link:hover {
  color: #1976d2;
}

.pricing-note {
  font-size: 0.9vw;
  color: #666;
  text-align: center;
  margin: 1.5vw 0;
}

.back-button-container {
  text-align: center;
}

.back-button {
  padding: 0.5vw 2vw;
  font-size: 1vw;
  color: #666;
  background: white;
  border: 0.1vw solid #ddd;
  border-radius: 0.3vw;
  cursor: pointer;
  transition: all 0.3s ease;
}

.back-button:hover {
  background: #f5f5f5;
  border-color: #999;
}

.section-title {
  margin: 0 0 1.2vw;
  color: #333;
  font-weight: 500;
}

.thumbnail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(4vw, 1fr));
  gap: 0.8vw;
  padding: 0.8vw;
  background: #fff;
  border-radius: 0.4vw;
}

.thumbnail-item {
  position: relative;
  aspect-ratio: 1;
  border: 0.1vw solid transparent;
  border-radius: 0.4vw;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #f8f9fa;
}

.thumbnail-item:hover {
  border-color: var(--primary-color);
  box-shadow: 0 0.1vw 0.4vw rgba(33, 150, 243, 0.2);
}

.thumbnail-item.selected {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 0.15vw rgba(33, 150, 243, 0.2);
}

.thumbnail-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* 选择指示器样式 */
.selection-indicator {
  position: absolute;
  top: 0.4vw;
  right: 0.4vw;
  width: 1vw;
  height: 1vw;
}

.base-circle {
  width: 100%;
  height: 100%;
  border: 0.1vw solid var(--primary-color);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  transition: all 0.2s ease;
}

.base-circle.selected {
  background: var(--primary-color);
  border-color: var(--primary-color);
}

.checkmark {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-size: 0.7vw;
  pointer-events: none;
}

/* Pet name tags */
.pet-name-tag {
  position: absolute;
  bottom: 0.2vw;
  left: 0.2vw;
  right: 0.2vw;
  background: rgba(76, 175, 80, 0.9);
  color: white;
  font-size: 0.6vw;
  padding: 0.2vw;
  text-align: center;
  border-radius: 0.2vw;
  font-weight: 500;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.no-name-tag {
  position: absolute;
  bottom: 0.2vw;
  left: 0.2vw;
  right: 0.2vw;
  background: rgba(244, 67, 54, 0.9);
  color: white;
  font-size: 0.6vw;
  padding: 0.2vw;
  text-align: center;
  border-radius: 0.2vw;
  font-weight: 500;
}

/* 上传按钮样式 */
.upload-card {
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0.1vw dashed #ccc;
  background: rgba(255,255,255,0.8);
}

.upload-card:hover {
  border-color: var(--primary-color);
  background: rgba(33, 150, 243, 0.05);
}

.upload-content {
  text-align: center;
  color: #666;
}

.plus-icon {
  font-size: 1.2vw;
  display: block;
  margin-bottom: 0.2vw;
  color: var(--primary-color);
}

/* 操作按钮 */
.action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 0.8vw;
  margin-top: 1.6vw;
  padding-top: 1.6vw;
  border-top: 0.05vw solid #eee;
}

.btn {
  padding: 0.6vw, 1.2vw;
  border: none;
  border-radius: 0.3vw;
  font-size: 0.8vw;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn.primary {
  background: var(--primary-color);
  color: white;
}

.btn.primary:hover {
  background: #1976d2;
}

.btn.secondary {
  background: #f8f9fa;
  color: #666;
  border: 0.05vw solid #ddd;
}

.btn.secondary:hover {
  background: #f1f3f5;
}

/* 全屏预览 */
.fullscreen-preview {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.fullscreen-image {
  max-width: 90%;
  max-height: 90%;
  object-fit: contain;
}
.content-center{
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1vw;
}
.content-right{
  padding: 1vw;
  margin-top: 3.35vw;
}
.button-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 2vw;
  padding: 0 2vw;
}

.nav-arrow {
  width: 3vw;
  height: 3vw;
  border-radius: 50%;
  border: 0.15vw solid #333;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.nav-arrow:hover {
  background: #f5f5f5;
  transform: scale(1.1);
}

.nav-arrow svg {
  width: 1.5vw;
  height: 1.5vw;
}

.customization-text {
  font-size: 1.1vw;
  color: #666;
  text-align: center;
}
.toggle-switch-container {
  margin: 1.5vw 0;
}

.toggle-options {
  position: relative;
  display: inline-flex;
  background: #f5f5f5;
  border-radius: 2vw;
  padding: 0.4vw;
  transition: all 0.3s ease;
}

.toggle-option {
  position: relative;
  padding: 0.8vw 2vw;
  border-radius: 1.5vw;
  cursor: pointer;
  z-index: 2;
  transition: color 0.3s ease;
  font-size: 1.1vw;
  user-select: none;
}

.slider-track {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #ffffff;
  border-radius: 1.5vw;
  box-shadow: 0 0.2vw 0.8vw rgba(0,0,0,0.1);
}

.slider-thumb {
  position: absolute;
  top: 0.4vw;
  height: calc(100% - 0.8vw);
  width: calc(50% - 0.8vw);
  background: #cfc5b3;
  border-radius: 1.3vw;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1;
}

.toggle-option.active {
  color: #ffffff;
}

.slider-thumb.uploaded {
  transform: translateX(0.4vw);
}

.slider-thumb.social {
  transform: translateX(calc(100% + 0.4vw));
}
.or-divider-container {
  display: none;
  position: absolute;
  text-align: center;
  transform: translate(-50%,0.25vw);
  left:50%;
  z-index: 999;
}

.or-divider-background {
  display: none;
  position: absolute;
  top: 50%;
  left: 50%;
  width: 50px;
  height: 50px;
  background-color: pink;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  z-index: -1;
}

.or-divider {
  position: relative;
  font-size: 2vw;
  font-weight: bold;
  z-index: 1;
}

.independent-style-section {
  margin: 1.5vw 0;
  padding: 1.2vw;
  background: #f8f9fa;
  border: 0.05vw solid #e9ecef;
  border-radius: 0.5vw;
  box-shadow: 0 0.1vw 0.3vw rgba(0,0,0,0.05);
}

.independent-style-section .style-link {
  margin-top: 0;
}

.independent-style-section .style-link a {
  font-weight: 500;
  color: #495057;
  font-size: 15px;
}

.independent-style-section .style-link a:hover {
  color: #212529;
}

@media (max-width: 768px) {
  .image-selector-container{
    max-width: 100%;
    padding: 0 4vw;
  }
  .content-left{
    width: 100%;
    display: flex;
    justify-content: center;
  }
  .content-right{
    width: 80%;
  }

  .recommended-angle-hint-btn {
    padding: 1.5vw 4vw;
    font-size: 3.5vw;
    border-radius: 5vw;
    gap: 2vw;
  }

  .recommended-angle-hint-btn svg {
    width: 4.5vw;
    height: 4.5vw;
  }

  .recommended-angle-trigger-container {
    margin-bottom: 3vw;
  }

  .main-preview-image {
    height: 39vw;
  }
  .content-top{
    text-align: center;
    width: 100%;
    margin: 0 auto;
    font-size: 7.1615vw;
  }
  .thumbnail-grid {
    grid-template-columns: repeat(auto-fill, minmax(21vw, 1fr));
  }
  .button-container {
    margin-top: 5.2292vw;
    padding: 0 4vw;
  }
  
  .nav-arrow {
    width: 8vw;
    height: 8vw;
    border: 0.3vw solid #333;
  }
  
  .nav-arrow svg {
    width: 4vw;
    height: 4vw;
  }
  
  .customization-text {
    font-size: 3vw;
  }
  .content-center{
    grid-template-columns: 1fr;
    place-items: center; /* 水平和垂直居中 */
  }
  .image-container{
    margin-top: 11.9479vw;
    height: 67.7083vw;
    width: 67.7083vw;
  }
  .empty-prompt {
    font-size: 2.9479vw;
  }
  .selection-indicator{
    top: 1.0417vw;
    right: 1.0417vw;
    width: 10vw;
    height: 10vw;
  }
  .upload-text{
    font-size: 2.9479vw;
  }
  .plus-icon{
    font-size: 4vw;
  }
  .checkmark{
    font-size: 5vw;
  }
  
  .pet-name-tag {
    bottom: 1vw;
    left: 1vw;
    right: 1vw;
    font-size: 2.5vw;
    padding: 1vw;
    border-radius: 1vw;
  }
  
  .no-name-tag {
    bottom: 1vw;
    left: 1vw;
    right: 1vw;
    font-size: 2.5vw;
    padding: 1vw;
    border-radius: 1vw;
  }
  .toggle-options {
    border-radius: 4vw;
    padding: 0.8vw;
  }

  .toggle-option {
    padding: 1.5vw 4vw;
    font-size: 3.5vw;
    border-radius: 3vw;
  }
  .or-divider {
    font-size: 5vw;
  }

  .slider-thumb {
    top: 0.8vw;
    height: calc(100% - 1.6vw);
    width: calc(50% - 1.6vw);
    border-radius: 2.6vw;
  }

  .slider-thumb.uploaded {
    transform: translateX(0.8vw);
  }

  .slider-thumb.social {
    transform: translateX(calc(100% + 0.8vw));
  }

  /* History Button & Dropdown Mobile Styles */
  .history-button {
    right: 2vw;
    padding: 1vw;
  }
  
  .history-button svg {
    width: 6vw;
    height: 6vw;
  }
  
  .previous-accounts-dropdown {
    border-radius: 2vw;
    margin-top: 2vw;
    box-shadow: 0 1vw 3vw rgba(0,0,0,0.15);
  }
  
  .dropdown-header {
    padding: 3vw 4vw;
    font-size: 3.5vw;
  }
  
  .close-dropdown {
    font-size: 6vw;
  }
  
  .dropdown-list {
    max-height: 60vw;
  }
  
  .previous-account-item {
    padding: 4vw;
  }
  
  .acc-info {
    gap: 1vw;
  }

  .acc-platform {
    font-size: 2.5vw;
  }
  
  .acc-username {
    font-size: 4vw;
  }
  
  .select-hint {
    font-size: 3.5vw;
    opacity: 1;
  }
  
  /* Social Media Mobile Styles */
  .social-media-area {
    min-height: 39vw;
  }
  
  .social-media-title {
    font-size: 3.5vw;
    margin-bottom: 4vw;
  }
  
  .form-group {
    margin-bottom: 3vw;
  }
  
  .platform-select,
  .username-input {
    font-size: 3.5vw;
    padding: 3vw;
    border-radius: 1vw;
  }
  
  .username-input {
    padding-left: 8vw;
    padding-right: 12vw;
  }
  
  .dropdown-icon {
    width: 4vw;
    height: 4vw;
    right: 3vw;
  }
  
  .input-icon {
    font-size: 4vw;
    left: 3vw;
  }
  
  .style-link {
    margin-top: 4vw;
  }
  
  .style-link a {
    font-size: 3vw;
  }
  
  /* Additional Customization Mobile Styles */
  .additional-customization-area {
    min-height: auto;
    padding: 4vw;
  }
  
  .additional-title {
    font-size: 4.5vw;
    margin-bottom: 4vw;
  }
  
  .customization-option {
    padding: 3vw;
    margin-bottom: 3vw;
  }
  
  .option-header {
    margin-bottom: 2vw;
  }
  
  .option-title {
    font-size: 3.5vw;
  }
  
  .option-description {
    font-size: 3vw;
  }
  
  .toggle-switch {
    width: 10vw;
    height: 5.5vw;
  }
  
  .toggle-slider {
    border-radius: 5.5vw;
  }
  
  .toggle-slider:before {
    height: 4vw;
    width: 4vw;
    left: 0.75vw;
    bottom: 0.75vw;
  }
  
  input:checked + .toggle-slider:before {
    transform: translateX(4.5vw);
  }
  
  .view-outfit-link {
    font-size: 3vw;
    margin-bottom: 2vw;
  }
  
  .pricing-note {
    font-size: 3vw;
    margin: 4vw 0;
  }
  
  .back-button {
    padding: 2vw 6vw;
    font-size: 3.5vw;
    border-radius: 1vw;
  }
  
  /* Mobile Modal Styles */
  .modal-content {
    border-radius: 2vw;
    padding: 4vw;
    max-width: 95vw;
    max-height: 95vh;
  }
  
  /* Mobile Outfit Swiper Styles */
  .outfit-swiper-container {
    height: 70vw;
    padding: 4vw 0;
  }
  
  .swiper-slide {
    width: min(70vw, 250px);
    transform: scale(0.7);
  }
  
  .swiper-slide-active {
    transform: scale(1.0);
  }
  
  .swiper-slide-prev,
  .swiper-slide-next {
    transform: scale(0.85);
  }
  
  .outfit-navigation {
    position: fixed;
    top: 50%;
    transform: translateY(-50%);
    padding: 0 4vw;
  }
  
  .outfit-nav-btn {
    width: 10vw;
    height: 10vw;
    border: 0.5vw solid #333;
  }
  
  .outfit-nav-btn svg {
    width: 5vw;
    height: 5vw;
  }
  
  .outfit-back-container {
    position: relative;
    bottom: auto;
    left: auto;
    transform: none;
    margin-top: 4vw;
  }
  
  .outfit-back-btn {
    padding: 3vw 8vw;
    font-size: 3.5vw;
    border-radius: 2vw;
  }
}

/* Outfit Selector Page Styles */
.outfit-selector-page {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.5);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1001;
  padding: 2vw;
}

/* Outfit Confirmation Styles */
.outfit-confirmation {
  background: white;
  border-radius: 1vw;
  padding: 2vw;
  max-width: 25vw;
  min-width: 20vw;
  text-align: center;
  box-shadow: 0 0.5vw 2vw rgba(0,0,0,0.2);
}

.confirmation-header {
  margin-bottom: 1.5vw;
}

.confirmation-title {
  font-size: 1.2vw;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.confirmation-content {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.selected-style-name {
  font-size: 1vw;
  color: #666;
  margin-bottom: 1.5vw;
}

.selected-outfit-preview {
  width: 12vw;
  height: 12vw;
  border-radius: 1vw;
  overflow: hidden;
  margin-bottom: 1.5vw;
  border: 0.1vw solid #eee;
}

.selected-outfit-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.change-style-button {
  background: none;
  border: none;
  color: #666;
  text-decoration: underline;
  font-size: 0.9vw;
  cursor: pointer;
  margin-bottom: 1vw;
}

.change-style-button:hover {
  color: #333;
}

.confirmation-arrow {
  color: #ccc;
  margin-bottom: 1.5vw;
}

.confirmation-arrow svg {
  width: 1.5vw;
  height: 1.5vw;
}

.confirmation-back-container {
  text-align: center;
}

.confirmation-back-btn {
  padding: 0.8vw 2vw;
  background: white;
  border: 0.1vw solid #ddd;
  border-radius: 0.5vw;
  color: #666;
  cursor: pointer;
  font-size: 0.9vw;
}

.confirmation-back-btn:hover {
  background: #f5f5f5;
}

/* Outfit Swiper View Styles */
.outfit-swiper-view {
  width: 100%;
  height: 100%;
  background: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.outfit-header {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  padding: 1.5vw;
  background: white;
  border-bottom: 0.1vw solid #eee;
  text-align: center;
}

.outfit-title {
  font-size: 1.8vw;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.style-toggle-container {
  display: flex;
  justify-content: center;
  gap: 0.5vw;
  margin-top: 8vw;
  margin-bottom: 1.5vw;
}

.style-toggle-btn {
  padding: 0.8vw 2.5vw;
  border: none;
  border-radius: 2vw;
  cursor: pointer;
  font-size: 1vw;
  transition: all 0.3s ease;
  background: #f0f0f0;
  color: #666;
}

.style-toggle-btn.active {
  background: #c8a882;
  color: white;
}

.style-toggle-btn:hover {
  background: #e0e0e0;
}

.style-toggle-btn.active:hover {
  background: #b8966f;
}

.gender-display {
  text-align: center;
  margin-bottom: 2vw;
}

.gender-label {
  font-size: 1.2vw;
  font-weight: 600;
  color: #333;
}

.outfit-swiper-container {
  width: 100%;
  height: 30vw;
  margin-bottom: 1.5vw;
  padding: 2vw 0;
  overflow: hidden;
}

.outfit-swiper .swiper-wrapper {
  pointer-events: none;
  transform-style: preserve-3d;
}

.swiper-container {
  width: 100%;
  height: 100%;
  overflow: visible;
}

.swiper-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  transform-style: preserve-3d;
  pointer-events: none;
}

.swiper-slide {
  width: 300px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.7;
  transition: all 0.5s ease;
  transform: scale(0.8);
  pointer-events: none;
  transform-style: preserve-3d;
}

.swiper-slide-active {
  opacity: 1;
  transform: scale(1.2);
  z-index: 10;
}

.swiper-slide-prev,
.swiper-slide-next {
  opacity: 0.9;
  transform: scale(1.0);
  z-index: 5;
}

.outfit-card {
  width: 100%;
  height: 100%;
  position: relative;
  border-radius: 0.8vw;
  perspective: 1000px;
  cursor: pointer;
  /* Remove background and box-shadow from card container as it's now on front/back */
  background: transparent;
  box-shadow: none; 
  pointer-events: auto;
}

.card-inner {
  position: relative;
  width: 100%;
  height: 100%;
  text-align: center;
  transition: transform 0.6s;
  transform-style: preserve-3d;
}

/* Desktop: Hover effect to flip */
@media (min-width: 769px) {
  .outfit-card:hover .card-inner {
    transform: rotateY(180deg);
  }
}

.card-front, .card-back {
  position: absolute;
  width: 100%;
  height: 100%;
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  border-radius: 0.8vw;
  overflow: hidden;
  background: white;
  box-shadow: 0 0.2vw 1vw rgba(0,0,0,0.1);
}

.card-back {
  transform: rotateY(180deg);
}

/* Maintain original hover effect on the faces */
.outfit-card:hover .card-front,
.outfit-card:hover .card-back {
  box-shadow: 0 0.4vw 2vw rgba(0,0,0,0.15);
}

.outfit-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.outfit-navigation {
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 2vw;
  pointer-events: none;
}

.outfit-nav-btn {
  width: 3.5vw;
  height: 3.5vw;
  border-radius: 50%;
  border: 0.15vw solid #333;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  pointer-events: all;
  box-shadow: 0 0.2vw 0.5vw rgba(0,0,0,0.1);
}

.outfit-nav-btn:hover {
  background: #f5f5f5;
  transform: scale(1.1);
}

.outfit-nav-btn svg {
  width: 1.5vw;
  height: 1.5vw;
}

.outfit-back-container {
  position: absolute;
  bottom: 2vw;
  left: 50%;
  transform: translateX(-50%);
}

.outfit-back-btn {
  padding: 0.8vw 2.5vw;
  font-size: 1vw;
  color: #666;
  background: white;
  border: 0.1vw solid #ddd;
  border-radius: 0.5vw;
  cursor: pointer;
  transition: all 0.3s ease;
}

.outfit-back-btn:hover {
  background: #f5f5f5;
  border-color: #999;
}

@media (max-width: 768px) {
  .outfit-selector-page {
    padding: 4vw;
  }
  
  .outfit-confirmation {
    max-width: 85vw;
    min-width: 75vw;
    padding: 6vw;
    border-radius: 3vw;
  }
  
  .confirmation-title {
    font-size: 4vw;
  }
  
  .selected-style-name {
    font-size: 3.5vw;
    margin-bottom: 4vw;
  }
  
  .selected-outfit-preview {
    width: 35vw;
    height: 35vw;
    border-radius: 3vw;
    margin-bottom: 4vw;
  }
  
  .change-style-button {
    font-size: 3vw;
    margin-bottom: 3vw;
  }
  
  .confirmation-arrow svg {
    width: 4vw;
    height: 4vw;
  }
  
  .confirmation-back-btn {
    padding: 2.5vw 6vw;
    font-size: 3vw;
    border-radius: 2vw;
  }
  
  .outfit-header {
    padding: 4vw;
    position: relative;
  }
  
  .outfit-title {
    font-size: 4.5vw;
  }
  
  .style-toggle-container {
    margin-top: 4vw;
    gap: 2vw;
  }
  
  .style-toggle-btn {
    padding: 3vw 6vw;
    font-size: 3.5vw;
    border-radius: 4vw;
  }
  
  .gender-label {
    font-size: 4vw;
  }
  
  .outfit-swiper-container {
    height: 70vw;
  }
  
  .outfit-navigation {
    position: fixed;
    top: 50%;
    transform: translateY(-50%);
    padding: 0 4vw;
  }
  
  .outfit-nav-btn {
    width: 10vw;
    height: 10vw;
    border: 0.5vw solid #333;
  }
  
  .outfit-nav-btn svg {
    width: 5vw;
    height: 5vw;
  }
  
  .outfit-back-container {
    position: relative;
    bottom: auto;
    left: auto;
    transform: none;
    margin-top: 4vw;
  }
  
  .outfit-back-btn {
    padding: 3vw 8vw;
    font-size: 3.5vw;
    border-radius: 2vw;
  }
}
</style>